/**
 * MUI Grid v4-to-v9 Compatibility Wrapper
 *
 * MUI v9 ships Grid v2, which removed/changed several v4 props:
 *   - `item` (no longer needed; every Grid is an item by default)
 *   - `xs`, `sm`, `md`, `lg`, `xl` (replaced by `size`)
 *   - `alignItems`, `justifyContent`, `alignContent` (moved to `sx`)
 *   - `direction` only supports 'row' | 'row-reverse' (column must go to sx)
 *   - `wrap` renamed to CSS flex-wrap via `sx`
 *
 * This wrapper translates the v4 API so that the 100+ existing Grid
 * usages in the codebase keep working without per-file changes.
 *
 * Once all files are migrated to the v9 API this file can be removed.
 */
import React from 'react';
import { Grid as MuiGrid } from '@mui/material';

const BREAKPOINT_KEYS = ['xs', 'sm', 'md', 'lg', 'xl'];

// Grid v2 only supports row directions natively
const V2_SUPPORTED_DIRECTIONS = new Set(['row', 'row-reverse']);

const GridCompat = React.forwardRef(function GridCompat(props, ref) {
  const {
    // v4 item prop (no-op in v2, just strip it)
    item,
    // v4 breakpoint sizing props
    xs,
    sm,
    md,
    lg,
    xl,
    // v4 container alignment props (moved to sx in v2)
    alignItems,
    justifyContent,
    justify, // v4 also accepted 'justify' as alias
    alignContent,
    // v4 zeroMinWidth
    zeroMinWidth,
    // direction and wrap need special handling
    direction,
    wrap,
    // Everything else passes through (container, spacing, sx, className, style, etc.)
    sx: sxProp,
    ...rest
  } = props;

  // Build size object from breakpoint props
  const size = {};
  let hasSize = false;
  for (const bp of BREAKPOINT_KEYS) {
    const val = props[bp];
    if (val !== undefined) {
      // v4: true means "auto" (grow), a number means columns, "auto" stays "auto"
      size[bp] = val === true ? 'grow' : val;
      hasSize = true;
    }
  }

  // Build sx additions for props that v2 no longer supports natively
  const sxAdditions = {};
  if (alignItems) sxAdditions.alignItems = alignItems;
  if (justifyContent || justify) sxAdditions.justifyContent = justifyContent || justify;
  if (alignContent) sxAdditions.alignContent = alignContent;
  if (zeroMinWidth) sxAdditions.minWidth = 0;

  // Handle direction: Grid v2 only supports 'row' and 'row-reverse'.
  // 'column' and 'column-reverse' must go through sx.flexDirection.
  let directionProp;
  if (direction && !V2_SUPPORTED_DIRECTIONS.has(direction)) {
    sxAdditions.flexDirection = direction;
  } else if (direction) {
    directionProp = direction;
  }

  // Handle wrap: Grid v2 accepts 'wrap' prop but v4 also uses 'nowrap'
  // which v2 supports natively. Pass through if defined.
  let wrapProp;
  if (wrap !== undefined) {
    wrapProp = wrap;
  }

  const hasSxAdditions = Object.keys(sxAdditions).length > 0;

  // Merge sx
  let mergedSx = sxProp;
  if (hasSxAdditions) {
    if (Array.isArray(sxProp)) {
      mergedSx = [sxAdditions, ...sxProp];
    } else if (sxProp) {
      mergedSx = [sxAdditions, sxProp];
    } else {
      mergedSx = sxAdditions;
    }
  }

  return (
    <MuiGrid
      ref={ref}
      {...rest}
      {...(directionProp ? { direction: directionProp } : {})}
      {...(wrapProp ? { wrap: wrapProp } : {})}
      {...(hasSize ? { size } : {})}
      {...(mergedSx !== undefined ? { sx: mergedSx } : {})}
    />
  );
});

export default GridCompat;
